<script setup lang="ts" generic="Item extends SelectItem, Multiple extends boolean = false">
import type { Ref } from 'vue';
import { useSlots } from 'vue';
import type { SelectItem, SelectItemSeparator } from '../../lib/list-selection/types';
import { useListMultipleSelect, useListSelect } from '../../lib/list-selection';
import { BaseList, BaseSelectItem, BaseSelectSeparator } from './index';

type Props = {
  items: (Item | SelectItemSeparator)[];
  gap?: number;
  showCheckbox?: boolean;
  pageMode?: boolean;
  multiline?: boolean;
  disabled?: boolean;
  multiple?: boolean;
};

type Id = Item['id'];

type ModelValue<Multiple extends boolean> = Multiple extends true
  ? Id[]
  : Id;

const props = withDefaults(defineProps<Props>(), {
  gap: 0,
  multiple: false
});

const emit = defineEmits<{
  itemClick: [SelectItem];
}>();

const value = defineModel<ModelValue<Multiple>>();

const {
  onHover,
  toggle,
  selectableItems,
  hoveredIndex,
  hoveredFromKeyboard
} = props.multiple
  ? useListMultipleSelect({
      items: () => props.items,
      modelValue: value as Ref<Id[]>,
      onChange: (ids: Id[]) => {
        value.value = ids;
      }
    })
  : useListSelect({
      items: () => props.items,
      required: true,
      modelValue: value as Ref<Id>,
      useKeyboardEvents: true,
      onChange: (id: Id) => {
        value.value = id;
      }
    });

const slots = useSlots();

function isSeparator(item: any): item is SelectItemSeparator {
  return item.type === 'separator';
}

defineExpose({ hoveredIndex });
</script>

<template>
  <base-list
    :gap="gap"
    data-qa="dropdown-list"
    :page-mode="pageMode"
    :scroll-to-active-index="hoveredFromKeyboard"
    :active-index="hoveredIndex"
    :items="selectableItems"
  >
    <template #item="{ index, item }">
      <base-select-separator
        v-if="isSeparator(item)"
        :label="item.label"
      />
      <slot
        v-else
        name="item"
        :item="item"
        :index="index"
        :on-hover="onHover"
        :toggle="toggle"
      >
        <base-select-item
          :title="item.name"
          :description="item.description"
          :icon="item.icon"
          :disabled="item.disabled"
          :data-qa="item.dataQa || 'list-item'"
          :show-checkbox="showCheckbox"
          :multiline="multiline"
          :hovered="item.userSelection.hovered"
          :selected="item.userSelection.selected"
          @click="emit('itemClick', item); toggle(item)"
          @mouseenter="onHover(index)"
        >
          <template
            v-for="(_, name) in slots"
            #[name]
          >
            <slot
              :name="name"
              :item="item"
            />
          </template>
        </base-select-item>
      </slot>
    </template>
  </base-list>
</template>

<i18n lang="json">{}</i18n>
